import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { hide, onHide, show } from "@intercom/messenger-js-sdk";

/**
 * Determine whether viewport is considered desktop or not based on a `900px` media query.
 * @returns {boolean} true when desktop
 * @example
 * const isDesktop = useIsDesktop();
 */
export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const isDesktopMatcher = window.matchMedia("(min-width: 900px)");
    const updateIsDesktop = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
    };
    // initially set on mount
    setIsDesktop(isDesktopMatcher.matches);
    // set up event listener
    isDesktopMatcher.addEventListener("change", updateIsDesktop);
    // clean up event listener
    return () => {
      isDesktopMatcher.removeEventListener("change", updateIsDesktop);
    };
  }, []);

  return isDesktop;
}

/**
 * Hook to manage the Intercom messenger widget. Automatically hides the messenger when
 * the component unmounts and provides a function to show the messenger.
 *
 * @returns {Object} Object containing the openIntercomMessenger function
 * @returns {Function} openIntercomMessenger - Function to show the Intercom messenger
 *
 * @example
 * const { openIntercomMessenger } = useIntercomMessenger();
 *
 * // show the messenger
 * openIntercomMessenger();
 */
export function useIntercomMessenger() {
  async function resetKeyboardResizeMode() {
    // reset keyboard resize to match our capacitor config when overridden on mobile
    // only available on iOS, Android default behavior is working correctly
    if (Capacitor.getPlatform() === "ios") {
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
    }
  }

  async function setKeyboardResizeModeNative() {
    // override our capacitor config to avoid keyboard covering message input on mobile
    // only available on iOS, Android default behavior is working correctly
    if (Capacitor.getPlatform() === "ios") {
      await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    }
  }

  // handle when user presses close icon within messenger
  useEffect(() => {
    const timeoutId = setTimeout(resetKeyboardResizeMode, 1000);
    onHide(() => {
      // clear any existing timeout when hiding
      clearTimeout(timeoutId);
      // set new timeout
      setTimeout(resetKeyboardResizeMode, 1000);
    });

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // handle when component unmounts
  useEffect(() => {
    return () => {
      hide();
      resetKeyboardResizeMode();
    };
  }, []);

  // handle when user open messenger
  async function openIntercomMessenger() {
    await setKeyboardResizeModeNative();
    show();
  }

  return { openIntercomMessenger };
}
